import { useState, useEffect } from 'react';

const API_URL = import.meta.env.VITE_API_URL || 'https://hello-reviews-api.b-9f2.workers.dev';

interface OutreachLog {
  id: number;
  business_name: string;
  contact_name?: string;
  contact_method?: string;
  message?: string;
  status?: string;
  created_at: string;
}

export default function BuilderFocus() {
  const [recentLogs, setRecentLogs] = useState<OutreachLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [streak, setStreak] = useState(0);
  const [lastContact, setLastContact] = useState<string | null>(null);
  const [todayCount, setTodayCount] = useState(0);
  const [weekCount, setWeekCount] = useState(0);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const response = await fetch(`${API_URL}/api/research/outreach?hidden=all`);
      const data = await response.json();

      if (data.success) {
        const logs = data.data || [];
        setRecentLogs(logs.slice(0, 5));
        calculateMetrics(logs);
      }
    } catch (err) {
      console.error('Failed to fetch logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateMetrics = (logs: OutreachLog[]) => {
    if (logs.length === 0) {
      setLastContact(null);
      setStreak(0);
      setTodayCount(0);
      setWeekCount(0);
      return;
    }

    // Last contact
    const mostRecent = logs[0];
    setLastContact(mostRecent.created_at);

    // Today's count
    const today = new Date().toISOString().split('T')[0];
    const todayLogs = logs.filter(log => log.created_at.startsWith(today));
    setTodayCount(todayLogs.length);

    // This week's count
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    const weekLogs = logs.filter(log => new Date(log.created_at) >= weekAgo);
    setWeekCount(weekLogs.length);

    // Calculate streak (consecutive days with at least one contact)
    const dates = logs.map(log => log.created_at.split('T')[0]);
    const uniqueDates = [...new Set(dates)].sort((a, b) => b.localeCompare(a));

    let currentStreak = 0;
    let checkDate = new Date();

    for (const date of uniqueDates) {
      const logDate = new Date(date);
      const daysDiff = Math.floor((checkDate.getTime() - logDate.getTime()) / (1000 * 60 * 60 * 24));

      if (daysDiff === currentStreak) {
        currentStreak++;
      } else {
        break;
      }
    }

    setStreak(currentStreak);
  };

  const getDaysSinceLastContact = () => {
    if (!lastContact) return null;
    const last = new Date(lastContact);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - last.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getUrgencyColor = () => {
    const days = getDaysSinceLastContact();
    if (days === null) return 'bg-red-100 border-red-500';
    if (days === 0) return 'bg-green-100 border-green-500';
    if (days === 1) return 'bg-yellow-100 border-yellow-500';
    if (days === 2) return 'bg-orange-100 border-orange-500';
    return 'bg-red-100 border-red-500';
  };

  const getUrgencyMessage = () => {
    const days = getDaysSinceLastContact();
    if (days === null) return "You haven't logged any conversations yet. Time to start!";
    if (days === 0) return "Great! You're in motion today.";
    if (days === 1) return "Yesterday was good. What about today?";
    if (days === 2) return "Two days ago. Don't lose momentum.";
    return `${days} days since your last conversation. This is critical.`;
  };

  const getStreakEmoji = () => {
    if (streak === 0) return '💤';
    if (streak < 3) return '🔥';
    if (streak < 7) return '🔥🔥';
    if (streak < 14) return '🔥🔥🔥';
    return '🚀';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 flex items-center justify-center">
        <div className="text-2xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Builder Focus</h1>
          <p className="text-xl text-gray-600">The only metric that matters right now</p>
        </div>

        {/* Main Status Card */}
        <div className={`border-4 ${getUrgencyColor()} rounded-2xl p-8 mb-6 shadow-xl`}>
          <div className="text-center">
            <div className="text-6xl mb-4">{getStreakEmoji()}</div>
            <h2 className="text-3xl font-bold text-gray-900 mb-3">
              {getUrgencyMessage()}
            </h2>

            {lastContact && (
              <p className="text-lg text-gray-600 mb-6">
                Last conversation: {new Date(lastContact).toLocaleDateString('en-US', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit'
                })}
              </p>
            )}

            <a
              href="#log"
              className="inline-block bg-blue-600 text-white px-8 py-4 rounded-lg font-bold text-xl hover:bg-blue-700 transition shadow-lg hover:shadow-xl"
            >
              Log a Conversation Now
            </a>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Streak */}
          <div className="bg-white rounded-xl p-6 shadow-lg text-center">
            <div className="text-5xl font-bold text-blue-600 mb-2">{streak}</div>
            <div className="text-gray-600 font-medium">Day Streak</div>
            <div className="text-sm text-gray-500 mt-1">
              {streak === 0 ? 'Start today!' : 'Keep it going!'}
            </div>
          </div>

          {/* Today */}
          <div className="bg-white rounded-xl p-6 shadow-lg text-center">
            <div className="text-5xl font-bold text-green-600 mb-2">{todayCount}</div>
            <div className="text-gray-600 font-medium">Today</div>
            <div className="text-sm text-gray-500 mt-1">
              {todayCount === 0 ? 'None yet' : todayCount === 1 ? 'Good start!' : 'On fire! 🔥'}
            </div>
          </div>

          {/* This Week */}
          <div className="bg-white rounded-xl p-6 shadow-lg text-center">
            <div className="text-5xl font-bold text-purple-600 mb-2">{weekCount}</div>
            <div className="text-gray-600 font-medium">This Week</div>
            <div className="text-sm text-gray-500 mt-1">
              {weekCount < 5 ? 'Goal: 5+' : 'Crushing it!'}
            </div>
          </div>
        </div>

        {/* Recent Conversations */}
        <div className="bg-white rounded-xl p-6 shadow-lg mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-2xl font-bold text-gray-900">Recent Conversations</h3>
            <a href="#log/recent" className="text-blue-600 hover:underline font-medium">
              View All →
            </a>
          </div>

          {recentLogs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p className="text-lg mb-4">No conversations logged yet.</p>
              <p className="text-sm">The best time to start was yesterday. The second best time is now.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentLogs.map((log) => (
                <div
                  key={log.id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-bold text-gray-900">{log.business_name}</h4>
                      {log.contact_name && (
                        <p className="text-sm text-gray-600">with {log.contact_name}</p>
                      )}
                      {log.message && (
                        <p className="text-sm text-gray-700 mt-1 line-clamp-2">{log.message}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-gray-500">
                        {new Date(log.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        })}
                      </div>
                      {log.status && (
                        <span className="inline-block mt-1 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                          {log.status.replace('_', ' ')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* The Truth */}
        <div className="bg-gray-900 text-white rounded-xl p-6 shadow-lg text-center">
          <p className="text-lg italic mb-2">
            "The product doesn't matter. The code doesn't matter. The only thing that matters is whether people actually want what you're building."
          </p>
          <p className="text-sm text-gray-400">
            You can only learn that through conversation.
          </p>
        </div>

        {/* Navigation */}
        <div className="mt-8 text-center">
          <a href="#home" className="text-blue-600 hover:underline font-medium">
            ← Back to Home
          </a>
        </div>
      </div>
    </div>
  );
}
